import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { Mail, Pencil, Plus, Trash2 } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useCanWriteContent } from "../use-admin-capabilities";
import { EmailTemplateEditor } from "./email-template-editor";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * CRUD de plantillas de email (campaña #11). Lista las plantillas (sistema +
 * personalizadas), permite crear/editar/borrar. Las de sistema no son borrables.
 */
export function EmailTemplateManager() {
  const { t } = useI18n();
  const canWriteContent = useCanWriteContent();
  // Editor URL-addressable (#15): `?new=1` crea, `?edit=<id>` edita. Pegar la URL
  // reabre el editor en el estado correcto (la plantilla se resuelve por id).
  const urlSearch = useSearch({ from: "/u/$u/admin/email" });
  const navigate = useNavigate();

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const creating = () => urlSearch().new === true;
  const editing = (): EmailTemplate | null => {
    const id = urlSearch().edit;
    if (!id) {
      return null;
    }
    return templatesQuery.data?.find((tpl) => tpl.id === id) ?? null;
  };
  const setCreating = (open: boolean) => {
    void navigate({
      to: "/admin/email",
      search: () => (open ? { new: true as const } : {}),
      replace: true,
    });
  };
  const setEditing = (tpl: EmailTemplate | null) => {
    void navigate({
      to: "/admin/email",
      search: () => (tpl ? { edit: tpl.id } : {}),
      replace: true,
    });
  };

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const closeEditor = () => {
    void navigate({ to: "/admin/email", search: () => ({}), replace: true });
  };

  const onSaved = async () => {
    closeEditor();
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        {/* Crear no tiene nada que LEER detrás: se oculta. */}
        <Show when={canWriteContent()}>
          <Button
            class="gap-2"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus class="h-4 w-4" />
            {t(msg`New Template`)}
          </Button>
        </Show>
      </div>

      <Show when={creating() || editing()}>
        <EmailTemplateEditor
          template={editing()}
          onSaved={onSaved}
          onCancel={() => {
            closeEditor();
          }}
        />
      </Show>

      <Show
        when={!templatesQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-20 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-3">
          <For each={templatesQuery.data ?? []}>
            {(tpl) => (
              <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="truncate font-semibold">{tpl.name}</span>
                    <Badge variant="secondary">{tpl.category}</Badge>
                    <Show when={tpl.isSystem}>
                      <Badge>{t(msg`System`)}</Badge>
                    </Show>
                    <Show when={!tpl.isActive}>
                      <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                    </Show>
                  </div>
                  <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                  <p class="mt-0.5 text-xs text-muted-foreground">
                    {(() => {
                      const { key } = tpl;
                      const version = tpl.currentVersion;
                      return t(msg`Key: ${key} · v${version}`);
                    })()}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-1"
                    onClick={() => {
                      setEditing(tpl);
                    }}
                  >
                    <Pencil class="h-4 w-4" />
                    {canWriteContent() ? t(msg`Edit`) : t(msg`View`)}
                  </Button>
                  <Show when={!tpl.isSystem && canWriteContent()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1 text-destructive"
                      disabled={deleteMutation.isPending}
                      onClick={() => handleDelete(tpl)}
                    >
                      <Trash2 class="h-4 w-4" />
                      {t(msg`Delete`)}
                    </Button>
                  </Show>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
